import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { roleGuard, tabGuard } from './core/guards/role.guard';
import { UserRole } from './core/models/user.model';
import { AuthService } from './core/services/auth.service';
import { OPPORTUNITY_TABS, TAB, UNIT_EDITOR_TABS } from './core/access/tabs';

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () =>
      import('./features/auth/login/login.component').then((m) => m.LoginComponent),
  },
  {
    path: 'verify-code',
    loadComponent: () =>
      import('./features/auth/verify-code/verify-code.component').then(
        (m) => m.VerifyCodeComponent,
      ),
  },
  {
    // O termo para imprimir/salvar em PDF abre sem a casca do portal (menu, cabeçalho).
    path: 'inspections/:id/print',
    canActivate: [authGuard, tabGuard(TAB.INSPECTIONS)],
    loadComponent: () =>
      import('./features/inspections/inspection-print/inspection-print.component').then(
        (m) => m.InspectionPrintComponent,
      ),
  },
  {
    path: '',
    canActivate: [authGuard],
    loadComponent: () => import('./shell/shell.component').then((m) => m.ShellComponent),
    children: [
      {
        path: '',
        redirectTo: 'overview',
        pathMatch: 'full',
      },
      {
        path: 'overview',
        loadComponent: () =>
          import('./features/overview/overview.component').then((m) => m.OverviewComponent),
      },
      {
        path: 'dashboard',
        redirectTo: 'overview',
        pathMatch: 'full',
      },
      {
        path: 'apartments',
        canActivate: [tabGuard(TAB.APARTMENTS, TAB.SITE, TAB.AVAILABILITY)],
        loadComponent: () =>
          import('./features/apartments/apartment-list/apartment-list.component').then(
            (m) => m.ApartmentListComponent,
          ),
      },
      {
        path: 'apartments/create',
        canActivate: [tabGuard(...UNIT_EDITOR_TABS)],
        loadComponent: () =>
          import('./features/apartments/apartment-form/apartment-form.component').then(
            (m) => m.ApartmentFormComponent,
          ),
      },
      {
        path: 'apartments/:id/edit',
        canActivate: [tabGuard(...UNIT_EDITOR_TABS)],
        loadComponent: () =>
          import('./features/apartments/apartment-form/apartment-form.component').then(
            (m) => m.ApartmentFormComponent,
          ),
      },
      {
        path: 'financial-entries',
        canActivate: [tabGuard(TAB.FINANCIAL_ENTRIES)],
        loadComponent: () =>
          import('./features/financial-entries/financial-entries.component').then(
            (m) => m.FinancialEntriesComponent,
          ),
      },
      {
        path: 'water-meter',
        canActivate: [tabGuard(TAB.WATER_METER)],
        loadComponent: () =>
          import('./features/water-meter/water-meter.component').then((m) => m.WaterMeterComponent),
      },
      {
        path: 'inspections',
        canActivate: [tabGuard(TAB.INSPECTIONS)],
        loadComponent: () =>
          import('./features/inspections/inspection-list/inspection-list.component').then(
            (m) => m.InspectionListComponent,
          ),
      },
      {
        path: 'inspections/:id',
        canActivate: [tabGuard(TAB.INSPECTIONS)],
        loadComponent: () =>
          import('./features/inspections/inspection-editor/inspection-editor.component').then(
            (m) => m.InspectionEditorComponent,
          ),
      },
      {
        path: 'building-notes',
        canActivate: [tabGuard(TAB.BUILDING_NOTES)],
        loadComponent: () =>
          import('./features/building-notes/building-notes.component').then(
            (m) => m.BuildingNotesComponent,
          ),
      },
      {
        path: 'teams',
        canActivate: [roleGuard([UserRole.ADMIN])],
        loadComponent: () =>
          import('./features/teams/teams-page/teams-page.component').then(
            (m) => m.TeamsPageComponent,
          ),
      },
      {
        path: 'community',
        canActivate: [tabGuard(TAB.COMMUNITY)],
        loadComponent: () =>
          import('./features/community/community.component').then((m) => m.CommunityComponent),
      },
      {
        path: 'occupancy-map',
        canActivate: [tabGuard(TAB.OCCUPANCY_MAP)],
        loadComponent: () =>
          import('./features/occupancy-map/occupancy-map.component').then(
            (m) => m.OccupancyMapComponent,
          ),
      },
      {
        path: 'apartment-models',
        canActivate: [tabGuard(TAB.APARTMENTS, TAB.SITE, TAB.AVAILABILITY)],
        loadComponent: () =>
          import('./features/apartment-models/apartment-model-list/apartment-model-list.component').then(
            (m) => m.ApartmentModelListComponent,
          ),
      },
      {
        path: 'apartment-models/create',
        canActivate: [tabGuard(...UNIT_EDITOR_TABS)],
        loadComponent: () =>
          import('./features/apartment-models/apartment-model-form/apartment-model-form.component').then(
            (m) => m.ApartmentModelFormComponent,
          ),
      },
      {
        path: 'apartment-models/:id/edit',
        canActivate: [tabGuard(...UNIT_EDITOR_TABS)],
        loadComponent: () =>
          import('./features/apartment-models/apartment-model-form/apartment-model-form.component').then(
            (m) => m.ApartmentModelFormComponent,
          ),
      },
      {
        // Virou aba da Página do Site. O redirect preserva links e favoritos antigos.
        path: 'amenities',
        pathMatch: 'full',
        redirectTo: () => inject(Router).parseUrl('/site?view=comodidades'),
      },
      {
        // Virou aba da Página do Site. O redirect preserva links e favoritos antigos.
        path: 'proximities',
        pathMatch: 'full',
        redirectTo: () => inject(Router).parseUrl('/site?view=proximidades'),
      },
      {
        // Virou aba da Página do Site. O redirect preserva links e favoritos antigos.
        path: 'services',
        pathMatch: 'full',
        redirectTo: () => inject(Router).parseUrl('/site?view=servicos'),
      },
      {
        path: 'gallery',
        canActivate: [tabGuard(TAB.GALLERY)],
        loadComponent: () =>
          import('./features/gallery/gallery-list/gallery-list.component').then(
            (m) => m.GalleryListComponent,
          ),
      },
      {
        path: 'gallery/tags',
        canActivate: [tabGuard(TAB.GALLERY)],
        loadComponent: () =>
          import('./features/gallery/tag-list/tag-list.component').then((m) => m.TagListComponent),
      },
      {
        path: 'site',
        canActivate: [tabGuard(TAB.SITE)],
        loadComponent: () =>
          import('./features/site-workspace/site-workspace.component').then(
            (m) => m.SiteWorkspaceComponent,
          ),
      },
      {
        // Virou aba da Página do Site. O redirect preserva links e favoritos antigos.
        path: 'external',
        pathMatch: 'full',
        redirectTo: () => inject(Router).parseUrl('/site?view=condominio'),
      },
      {
        path: 'contacts',
        canActivate: [tabGuard(TAB.CONTACTS, TAB.RESIDENTS)],
        loadComponent: () =>
          import('./features/contacts/contact-list/contact-list.component').then(
            (m) => m.ContactListComponent,
          ),
      },
      {
        // Mesma lista, recortada para a Gestão predial: só cliente e ex-cliente.
        path: 'residents',
        canActivate: [tabGuard(TAB.RESIDENTS)],
        data: { audience: 'residents' },
        loadComponent: () =>
          import('./features/contacts/contact-list/contact-list.component').then(
            (m) => m.ContactListComponent,
          ),
      },
      {
        // O cadastro de contato virou diálogo, como já era dentro da oportunidade e da
        // tarefa. A rota continua existindo para não quebrar link salvo e busca global:
        // leva à lista com o diálogo aberto.
        path: 'contacts/create',
        pathMatch: 'full',
        redirectTo: () => inject(Router).parseUrl('/contacts?novo=1'),
      },
      {
        path: 'contacts/:id',
        canActivate: [tabGuard(TAB.CONTACTS, TAB.RESIDENTS)],
        loadComponent: () =>
          import('./features/contacts/contact-detail/contact-detail.component').then(
            (m) => m.ContactDetailComponent,
          ),
      },
      {
        path: 'contacts/:id/edit',
        pathMatch: 'full',
        redirectTo: (rota) => inject(Router).parseUrl(`/contacts?editar=${rota.params['id']}`),
      },
      {
        path: 'contracts',
        // Contrato é assunto do Comercial; a Gestão predial vê Moradores, não contratos.
        canActivate: [tabGuard(TAB.CONTRACTS)],
        loadComponent: () =>
          import('./features/contracts/contract-list/contract-list.component').then(
            (m) => m.ContractListComponent,
          ),
      },
      {
        path: 'availability',
        canActivate: [tabGuard(TAB.AVAILABILITY)],
        loadComponent: () =>
          import('./features/availability/availability.component').then(
            (m) => m.AvailabilityComponent,
          ),
      },
      {
        path: 'commercial',
        canActivate: [tabGuard(TAB.COMMERCIAL)],
        loadComponent: () =>
          import('./features/commercial-workspace/commercial-workspace.component').then(
            (m) => m.CommercialWorkspaceComponent,
          ),
      },
      {
        // Oportunidades deixou de ser destino próprio e virou aba do espaço Comercial.
        // O redirect preserva links e favoritos antigos, caindo direto na Tabela.
        path: 'opportunities',
        pathMatch: 'full',
        redirectTo: () => inject(Router).parseUrl('/commercial?view=table'),
      },
      {
        path: 'opportunities/stages',
        canActivate: [tabGuard(TAB.COMMERCIAL)],
        loadComponent: () =>
          import('./features/opportunities/opportunity-stages/opportunity-stages.component').then(
            (m) => m.OpportunityStagesComponent,
          ),
      },
      {
        path: 'opportunities/loss-reasons',
        canActivate: [tabGuard(TAB.COMMERCIAL)],
        loadComponent: () =>
          import('./features/opportunities/opportunity-loss-reasons/opportunity-loss-reasons.component').then(
            (m) => m.OpportunityLossReasonsComponent,
          ),
      },
      {
        path: 'opportunities/create',
        canActivate: [tabGuard(...OPPORTUNITY_TABS)],
        loadComponent: () =>
          import('./features/opportunities/opportunity-form/opportunity-form.component').then(
            (m) => m.OpportunityFormComponent,
          ),
      },
      {
        path: 'tasks',
        canActivate: [tabGuard(TAB.TASKS)],
        loadComponent: () =>
          import('./features/opportunities/tasks/task-list-page/task-list-page.component').then(
            (m) => m.TaskListPageComponent,
          ),
      },
      {
        path: 'opportunities/:id',
        canActivate: [tabGuard(...OPPORTUNITY_TABS)],
        loadComponent: () =>
          import('./features/opportunities/opportunity-detail/opportunity-detail.component').then(
            (m) => m.OpportunityDetailComponent,
          ),
      },
      {
        path: 'opportunities/:id/edit',
        canActivate: [tabGuard(...OPPORTUNITY_TABS)],
        loadComponent: () =>
          import('./features/opportunities/opportunity-detail/opportunity-detail.component').then(
            (m) => m.OpportunityDetailComponent,
          ),
      },
      {
        path: 'appointments',
        canActivate: [tabGuard(TAB.APPOINTMENTS)],
        children: [
          {
            path: '',
            redirectTo: 'calendar',
            pathMatch: 'full',
          },
          {
            path: 'list',
            loadComponent: () =>
              import('./features/appointments/appointment-list/appointment-list.component').then(
                (m) => m.AppointmentListComponent,
              ),
          },
          {
            path: 'calendar',
            loadComponent: () =>
              import('./features/appointments/appointment-calendar-page/appointment-calendar-page.component').then(
                (m) => m.AppointmentCalendarPageComponent,
              ),
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./features/appointments/appointment-detail/appointment-detail.component').then(
                (m) => m.AppointmentDetailComponent,
              ),
          },
        ],
      },
      {
        path: 'attendance',
        canActivate: [tabGuard(TAB.ATTENDANCE, TAB.MAINTENANCE)],
        children: [
          {
            // Ouvidoria primeiro: é a fila que PEDE alguém. A conversa que virou reclamação já
            // foi encerrada pela IA; quem abre esta tela vem tratar o que ficou registrado.
            path: '',
            pathMatch: 'full',
            // Quem só tem Manutenções cai nelas; quem tem Atendimento, na Ouvidoria.
            redirectTo: () =>
              inject(AuthService).hasTab(TAB.ATTENDANCE) ? 'grievance' : 'maintenance',
          },
          {
            // Rota sem caminho: a casca do Atendimento envolve SÓ Conversas e Ouvidoria.
            //
            // Antes ela estava no `/attendance`, e com isso Manutenções e Tipos de manutenção —
            // que também moram aqui embaixo — apareciam com o cabeçalho "Atendimento" e o
            // seletor Conversas/Ouvidoria em cima. Eram telas de outra coisa usando a navegação
            // de duas telas às quais não pertencem.
            path: '',
            canActivate: [tabGuard(TAB.ATTENDANCE)],
            loadComponent: () =>
              import('./features/attendance/shell/attendance-shell.component').then(
                (m) => m.AttendanceShellComponent,
              ),
            children: [
              {
                path: 'conversations',
                loadComponent: () =>
                  import('./features/attendance/conversations/conversations-page.component').then(
                    (m) => m.ConversationsPageComponent,
                  ),
              },
              {
                path: 'grievance',
                loadComponent: () =>
                  import('./features/attendance/grievance/grievance-list/grievance-list.component').then(
                    (m) => m.GrievanceListComponent,
                  ),
              },
            ],
          },
          {
            path: 'maintenance-types',
            canActivate: [tabGuard(TAB.MAINTENANCE, TAB.ATTENDANCE)],
            loadComponent: () =>
              import('./features/attendance/maintenance/maintenance-types/maintenance-types.component').then(
                (m) => m.MaintenanceTypesComponent,
              ),
          },
          {
            path: 'maintenance',
            canActivate: [tabGuard(TAB.MAINTENANCE)],
            loadComponent: () =>
              import('./features/attendance/maintenance/maintenance-list/maintenance-list.component').then(
                (m) => m.MaintenanceListComponent,
              ),
          },
        ],
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./features/users/user-profile/user-profile.component').then(
            (m) => m.UserProfileComponent,
          ),
      },
      {
        path: 'users',
        canActivate: [roleGuard([UserRole.ADMIN])],
        loadComponent: () =>
          import('./features/users/user-list/user-list.component').then((m) => m.UserListComponent),
      },
      {
        path: 'vacations',
        canActivate: [tabGuard(TAB.VACATIONS)],
        loadComponent: () =>
          import('./features/vacations/vacations.component').then((m) => m.VacationsComponent),
      },
      {
        path: 'api-keys',
        canActivate: [roleGuard([UserRole.ADMIN])],
        loadComponent: () =>
          import('./features/api-keys/api-key-list/api-key-list.component').then(
            (m) => m.ApiKeyListComponent,
          ),
      },
      {
        path: 'announcements',
        canActivate: [tabGuard(TAB.ANNOUNCEMENTS)],
        loadComponent: () =>
          import('./features/announcements/announcement-list/announcement-list.component').then(
            (m) => m.AnnouncementListComponent,
          ),
      },
      {
        path: 'reservations',
        canActivate: [tabGuard(TAB.RESERVATIONS)],
        loadComponent: () =>
          import('./features/reservations/reservation-list/reservation-list.component').then(
            (m) => m.ReservationListComponent,
          ),
      },
      {
        // Endereço desconhecido: explica, dentro da casca, em vez de cair calado na home.
        // Mandar para o login (como era antes) fazia um link velho parecer sessão expirada;
        // mandar para a Visão Geral fazia parecer que o link tinha funcionado. Continua atrás
        // do authGuard, então quem não está logado vai para o login como sempre.
        path: '**',
        loadComponent: () =>
          import('./features/not-found/not-found.component').then((m) => m.NotFoundComponent),
      },
    ],
  },
];
